import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from '../GoogleMapsProvider';
import { DEFAULT_ROUTE_PATH_OPTIONS } from '../../utils/mapArcs';
import { darkMapStyles, poiOffStyles } from './mapStyles';
import { PATH_IDEA, PATH_TEARDROP, PATH_LODGING, COLOR_IDEA, COLOR_LODGING } from '../../utils/mapMarkerPaths';
import MarkerInfoCard from './MarkerInfoCard';
import { useAuth } from '../../auth/AuthContext';
import { reportUsage } from '../../services/usageBeacon';

const containerStyle = { width: '100%', height: '100%' };

function fitMapToRoute(map, fitPoints, fitMaxZoom = 5) {
  if (!map || !window.google?.maps?.LatLngBounds || !fitPoints?.length) return;
  if (fitPoints.length === 1) {
    const [lat, lon] = fitPoints[0];
    map.setCenter({ lat, lng: lon });
    map.setZoom(fitMaxZoom);
    return;
  }
  const bounds = new window.google.maps.LatLngBounds();
  fitPoints.forEach(([lat, lon]) => bounds.extend({ lat, lng: lon }));
  map.fitBounds(bounds, { padding: 40, maxZoom: fitMaxZoom });
}

function GoogleMapInner({ center, zoom, fitPoints, fitKey, fitMaxZoom = 5, markers, polylines, geoJsonOverlay, geoJsonStyle, theme }) {
  const mapRef = useRef(null);
  const polylineRefs = useRef([]);
  const directionsCacheRef = useRef({});
  const directionsServiceRef = useRef(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [planOnly, setPlanOnly] = useState(false);
  const fitPointsRef = useRef(fitPoints);
  fitPointsRef.current = fitPoints;

  const fitMaxZoomRef = useRef(fitMaxZoom);
  fitMaxZoomRef.current = fitMaxZoom;

  const onMapLoad = React.useCallback((map) => {
    mapRef.current = map;
    setMapReady(true);
    reportUsage('google_maps', 'maps_load');  // billable Dynamic Map load
    fitMapToRoute(map, fitPointsRef.current, fitMaxZoomRef.current);
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const styles = theme === 'dark'
      ? (planOnly ? [...darkMapStyles, ...poiOffStyles] : darkMapStyles)
      : (planOnly ? poiOffStyles : []);
    mapRef.current.setOptions({ styles });
  }, [mapReady, planOnly, theme]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google) return;
    const map = mapRef.current;
    let cancelled = false;

    polylineRefs.current.forEach((p) => {
      if (p && p.setMap) p.setMap(null);
    });
    polylineRefs.current = [];

    // Replace a straight connector's path with a real road route via Google
    // Directions (cached). Leaves the straight fallback in place on any failure
    // (no key/API, ZERO_RESULTS for cross-ocean, quota, etc.) — degrade, never crash.
    const resolveRoute = (poly, origin, dest, travelMode) => {
      const key = `${origin.lat.toFixed(5)},${origin.lng.toFixed(5)}->${dest.lat.toFixed(5)},${dest.lng.toFixed(5)}:${travelMode}`;
      const cache = directionsCacheRef.current;
      if (cache[key]) {
        poly.setOptions({ geodesic: false });
        poly.setPath(cache[key]);
        return;
      }
      if (!directionsServiceRef.current) {
        if (!window.google.maps.DirectionsService) return;
        directionsServiceRef.current = new window.google.maps.DirectionsService();
      }
      const mode = window.google.maps.TravelMode?.[travelMode] || travelMode;
      reportUsage('google_maps', 'directions');  // billable — only on cache miss (real route call)
      directionsServiceRef.current.route({ origin, destination: dest, travelMode: mode }, (res, status) => {
        if (cancelled) return;
        if (status === 'OK' && res?.routes?.[0]?.overview_path?.length) {
          const routePath = res.routes[0].overview_path;
          cache[key] = routePath;
          if (poly && poly.setMap) {
            poly.setOptions({ geodesic: false });
            poly.setPath(routePath);
          }
        }
      });
    };

    (polylines || []).forEach((pl) => {
      if (!pl.positions || pl.positions.length < 2) return;
      const path = pl.positions.map(([lat, lon]) => ({ lat, lng: lon }));
      const opts = pl.pathOptions || DEFAULT_ROUTE_PATH_OPTIONS;
      const color = opts.color || '#c49b63';
      const opacity = opts.opacity ?? 0.85;
      const weight = opts.weight ?? 3;
      const polyOpts = { path, geodesic: true, strokeColor: color, strokeWeight: weight };
      if (opts.dashArray) {
        polyOpts.strokeOpacity = 0;
        polyOpts.icons = [{
          icon: { path: 'M 0,-1 0,1', strokeOpacity: opacity, strokeColor: color, scale: weight },
          offset: '0',
          repeat: '14px',
        }];
      } else {
        polyOpts.strokeOpacity = opacity;
      }
      const poly = new window.google.maps.Polyline(polyOpts);
      poly.setMap(map);
      polylineRefs.current.push(poly);

      if (pl.routable && path.length === 2) {
        resolveRoute(poly, path[0], path[1], pl.travelMode || 'DRIVING');
      }
    });

    return () => {
      cancelled = true;
      polylineRefs.current.forEach((p) => {
        if (p && p.setMap) p.setMap(null);
      });
      polylineRefs.current = [];
    };
  }, [mapReady, polylines, fitKey]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !fitPoints || fitPoints.length === 0) return;
    const map = mapRef.current;
    if (!window.google?.maps?.LatLngBounds) return;
    const maxZoom = fitMaxZoom ?? 5;

    if (fitPoints.length === 1) {
      const [lat, lon] = fitPoints[0];
      map.setCenter({ lat, lng: lon });
      map.setZoom(maxZoom);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    fitPoints.forEach(([lat, lon]) => bounds.extend({ lat, lng: lon }));
    map.fitBounds(bounds, { padding: 40, maxZoom });
  }, [mapReady, fitPoints, fitKey, fitMaxZoom]);

  const handleCenterClick = React.useCallback(() => {
    if (mapRef.current && fitPoints?.length) {
      fitMapToRoute(mapRef.current, fitPoints, fitMaxZoom);
    }
  }, [fitPoints, fitMaxZoom]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !geoJsonOverlay || !mapRef.current.data) return;
    const map = mapRef.current;
    map.data.addGeoJson(geoJsonOverlay);
    map.data.setStyle((feature) => {
      const props = {};
      const keys = ['ISO_A2', 'ISO_A2_EH', 'iso_a2'];
      keys.forEach((k) => {
        try {
          const v = feature.getProperty(k);
          if (v != null && v !== '') props[k] = v;
        } catch (_e) { /* ignore */ }
      });
      if (Object.keys(props).length === 0 && typeof feature.forEachProperty === 'function') {
        feature.forEachProperty((value, key) => { props[key] = value; });
      }
      const leafletLikeFeature = { properties: props };
      const style = typeof geoJsonStyle === 'function' ? geoJsonStyle(leafletLikeFeature) : {};
      const toHex = (v) => (v && String(v).startsWith('var(') ? '#e86c60' : v);
      return {
        strokeColor: toHex(style.color) || '#555',
        strokeWeight: style.weight ?? 1,
        fillColor: toHex(style.fillColor) || '#e86c60',
        fillOpacity: style.fillOpacity ?? 0,
      };
    });
    return () => {
      map.data.forEach((f) => map.data.remove(f));
    };
  }, [mapReady, geoJsonOverlay, geoJsonStyle]);

  const mapCenter = { lat: center[0], lng: center[1] };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={zoom}
      onLoad={onMapLoad}
      onClick={() => setSelectedMarker(null)}
      options={{
        streetViewControl: false,
        mapTypeControl: true,
        gestureHandling: 'greedy',
        styles: theme === 'dark'
          ? (planOnly ? [...darkMapStyles, ...poiOffStyles] : darkMapStyles)
          : (planOnly ? poiOffStyles : undefined),
      }}
    >
      {(markers || []).map((m, idx) => {
        const lat = Number(m.lat);
        const lon = Number(m.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        const isSelected = selectedMarker === idx;
        const isLodging = m.kind === 'lodging';
        const isIdea = m.kind === 'idea';
        const dotIcon = {
          path: isLodging ? PATH_LODGING : (isIdea ? PATH_TEARDROP : PATH_IDEA),
          scale: isLodging ? 1.5 : (isIdea ? 1.5 : 1.25),
          fillColor: isLodging ? COLOR_LODGING : COLOR_IDEA,
          fillOpacity: 1,
          strokeColor: 'rgba(255,255,255,0.9)',
          strokeWeight: 1,
          anchor: window.google?.maps?.Point ? new window.google.maps.Point(0, 0) : undefined,
        };
        // Category Lucide marker (0017) when provided; otherwise the legacy dot.
        const markerIcon = (m.iconUrl && window.google?.maps?.Size)
          ? {
              url: m.iconUrl,
              scaledSize: new window.google.maps.Size(30, 30),
              anchor: new window.google.maps.Point(15, 15),
            }
          : dotIcon;
        return (
          <React.Fragment key={`gm-marker-${idx}`}>
            <Marker
              position={{ lat, lng: lon }}
              icon={markerIcon}
              optimized={false}
              onClick={() => setSelectedMarker(isSelected ? null : idx)}
            />
            {isSelected && (
              <InfoWindow position={{ lat, lng: lon }} onCloseClick={() => setSelectedMarker(null)}>
                <MarkerInfoCard marker={m} map={mapRef.current} />
              </InfoWindow>
            )}
          </React.Fragment>
        );
      })}
    </GoogleMap>
    <div className="tp-map-overlay-btns">
      {fitPoints?.length > 0 && (
        <button
          type="button"
          className="tp-map-center-btn"
          onClick={handleCenterClick}
          title="Center on route"
          aria-label="Center map on route"
        >
          ⊞
        </button>
      )}
      <button
        type="button"
        className={`tp-map-poi-toggle${planOnly ? ' tp-map-poi-toggle--active' : ''}`}
        onClick={() => setPlanOnly(v => !v)}
        title={planOnly ? 'Show all Google places' : 'Hide Google places — show itinerary only'}
      >
        {planOnly ? 'All places' : 'Plan only'}
      </button>
    </div>
    </div>
  );
}

export default function GoogleMapView(props) {
  const { isLoaded, loadError } = useGoogleMaps();
  const { apiPaused } = useAuth();

  if (apiPaused) {
    return (
      <div style={{ ...containerStyle, display: 'grid', placeItems: 'center', background: 'var(--tp-bg)', textAlign: 'center', padding: 16 }}>
        <span style={{ color: 'var(--tp-muted)' }}>Maps are paused for the month to keep the app free during beta.</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ ...containerStyle, display: 'grid', placeItems: 'center', background: 'var(--tp-bg)' }}>
        <span style={{ color: 'var(--tp-muted)' }}>Map unavailable</span>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ ...containerStyle, display: 'grid', placeItems: 'center', background: 'var(--tp-bg)' }}>
        <span style={{ color: 'var(--tp-muted)' }}>Loading map…</span>
      </div>
    );
  }

  return <GoogleMapInner {...props} />;
}
